import { useEffect, useRef, useState } from "react";
import { Container, Form, Modal, Stack, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  DifficultyDisplay,
  EnjoymentDisplay,
  ErrorContainer,
  LoadingContainer,
  SHEET_DIFFICULTIES,
} from "../../components/MiscComponents";
import { sortListByProperties, numberWithCommas, getMapNameFromGoldenRun } from "./StatsUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { formatClearDuration } from "./StatsUtil";
import { Popover, Button, OverlayTrigger } from "react-bootstrap";
import { PlayerProfileNavbar } from "../../components/MiscComponents";
import { TimeIcon } from "../../components/Icons";

import { useQuery, useQueryClient } from "react-query";
import { getTopGoldenList, parseAxiosError } from "../../hooks/CelesteStatsApi";
import { PlayerGeneralStats } from "./Player";
import { useModal } from "../../hooks/useModal";
import { QuickEditMap } from "./QuickEditMap";
import { useUserSession } from "../../hooks/useUserSession";
import { useLocalStorage } from "../../hooks/useStorage";
import { getContrastTextColor } from "../../util/OtherUtils";

export default function TopGoldenListPage() {
  const { playerName } = useParams();
  const userSession = useUserSession();
  const [showEmptyTiers, setShowEmptyTiers] = useLocalStorage("topGoldenListShowEmptyTiers", false);
  const topGoldenListQuery = useQuery({
    queryKey: ["topGoldenList", playerName],
    queryFn: () => getTopGoldenList(playerName),
  });

  const modalRefs = {
    openEditModal: useRef(),
  };

  let title = playerName + "'s Top Golden List";
  document.title = title + " - Celeste Stats";

  if (topGoldenListQuery.isLoading) {
    return (
      <Container fluid>
        <Container>
          <PlayerGeneralStats
            playerName={playerName}
            activePage={"/player/" + playerName + "/top-golden-list"}
          />
        </Container>
        <LoadingContainer fluid centered />
      </Container>
    );
  } else if (topGoldenListQuery.isError) {
    let errorDetails = parseAxiosError(topGoldenListQuery.error);
    return (
      <Container fluid>
        <Container>
          <PlayerGeneralStats
            playerName={playerName}
            activePage={"/player/" + playerName + "/top-golden-list"}
          />
        </Container>
        <ErrorContainer message={errorDetails.message} fluid centered />
      </Container>
    );
  }

  console.log("Top Golden List -> ", topGoldenListQuery.data);

  //One column per tier, hardest first. Empty tiers are hidden unless showEmptyTiers is set.
  let groupedTopGoldenList = SHEET_DIFFICULTIES.filter((tier) => tier.id !== 0)
    .map((tier) => ({
      tier: tier,
      goldens: topGoldenListQuery.data.filter((golden) => golden.sheet_difficulty_id === tier.id),
    }))
    .filter((group) => showEmptyTiers || group.goldens.length > 0);

  console.log("Grouped Top Golden List -> ", groupedTopGoldenList);

  return (
    <>
      <Container>
        <PlayerGeneralStats
          playerName={playerName}
          activePage={"/player/" + playerName + "/top-golden-list"}
        />
      </Container>
      <Container fluid className="pe-5">
        <Form.Check
          type="checkbox"
          id="top-golden-list-show-empty-tiers"
          className="mb-2"
          label="Show empty tiers"
          checked={showEmptyTiers}
          onChange={(e) => setShowEmptyTiers(e.target.checked)}
        />
        {groupedTopGoldenList.length === 0 && <p>No cleared goldens yet.</p>}
        <div className="d-flex flex-row me-3">
          {groupedTopGoldenList.map((group) => (
            <TopGoldenListGroup
              key={group.tier.id}
              group={group}
              openEditModalRef={modalRefs.openEditModal}
              isOwnPage={userSession.isOwnPage(playerName)}
            />
          ))}
        </div>
      </Container>
      <ModalContainer playerName={playerName} modalRefs={modalRefs} />
    </>
  );
}

function TopGoldenListGroup({ group, openEditModalRef, isOwnPage }) {
  const headerStyle = {
    backgroundColor: group.tier.color,
    color: getContrastTextColor(group.tier.color),
    borderRight: "1px solid black",
    borderLeft: "1px solid black",
    borderTop: "1px solid black",
    alignSelf: "flex-start",
  };
  const tableStyle = {
    backgroundColor: group.tier.color + "!important",
    textAlign: "center",
    fontSize: "130%",
  };
  const borderStyle = {
    borderBottom: "2px solid black",
  };

  sortListByProperties(group.goldens, [["clear_duration", "DESC"], "map_name"]);

  return (
    <div className="mb-5 d-flex flex-column" style={headerStyle}>
      <TopGoldenListGroupHeader group={group} />
      <table style={tableStyle} className="px-2">
        <thead style={borderStyle}>
          <tr>
            <th style={{ minWidth: "100px", borderRight: "1px solid black" }}>Map</th>
            <th style={{ minWidth: "130px", borderRight: "1px solid black" }}>
              <div className="d-flex justify-content-center">
                <TimeIcon size="medium-small" />
              </div>
            </th>
            <th style={{ minWidth: "80px" }}>Video</th>
          </tr>
        </thead>
        <tbody>
          {group.goldens.map((golden) => (
            <TopGoldenListRow
              key={golden.id}
              golden={golden}
              openEditModalRef={openEditModalRef}
              isOwnPage={isOwnPage}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopGoldenListGroupHeader({ group }) {
  const borderStyle = {
    borderBottom: "2px solid black",
  };
  return (
    <div style={borderStyle}>
      <h2 className="mx-5 my-3" style={{ whiteSpace: "nowrap", textAlign: "center" }}>
        {group.tier.name}
      </h2>
    </div>
  );
}

function TopGoldenListRow({ golden, openEditModalRef, isOwnPage }) {
  const openEditModal = () => {
    document.body.click();
    openEditModalRef.current(golden.map_id);
  };

  //Links use the readable text color of the tier instead of the default link blue
  const linkColor = getContrastTextColor(golden.tier_color);
  const rowStyle = {
    backgroundColor: golden.tier_color,
    borderBottom: "1px solid black",
  };
  const cellStyle = {
    borderLeft: "1px solid black",
  };

  let mapName = getMapNameFromGoldenRun(golden);
  if (golden.description !== null) {
    mapName += " (" + golden.description + ")";
  }
  if (golden.is_fc && !golden.is_special) {
    mapName += " [FC]";
  }
  if (!golden.is_fc && golden.is_special) {
    mapName += " [SB]";
  }
  if (golden.is_fc && golden.is_special) {
    mapName += " [FC+SB]";
  }

  let dateString = golden.cleared_on !== null ? new Date(golden.cleared_on).toLocaleDateString() : "-";
  let deathsString = golden.clear_deaths !== null ? numberWithCommas(golden.clear_deaths) : "-";
  let timeString = golden.clear_duration !== null ? formatClearDuration(golden.clear_duration) : "-";

  const NamePopover = (
    <Popover id="basic-popover" style={{ maxWidth: "300px" }}>
      <Popover.Body>
        <div className="d-flex flex-column">
          <div>
            {golden.campaign_name ? (
              <>
                <span className="fw-bold">Campaign: </span>
                <Link to={"/player/" + golden.user_name + "/campaign/" + golden.campaign_id}>
                  {golden.campaign_name}
                </Link>
              </>
            ) : null}
          </div>
          <div>
            <span className="fw-bold">Map: </span>
            <Link to={"/player/" + golden.user_name + "/map/" + golden.map_id}>
              {getMapNameFromGoldenRun(golden)}
            </Link>{" "}
            {isOwnPage && (
              <Link style={{ color: "lightgray" }} onClick={openEditModal}>
                <FontAwesomeIcon icon={faEdit} />
              </Link>
            )}
          </div>
          <div>
            <span className="fw-bold">Cleared on: </span>
            {dateString}
          </div>
          <div>
            <span className="fw-bold">Time: </span>
            {timeString}
          </div>
          <div>
            <span className="fw-bold">Deaths: </span>
            {deathsString}
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  const proofElement =
    golden.proof_url !== null ? (
      <a href={golden.proof_url} style={{ textDecoration: "none", color: linkColor }}>
        ▶
      </a>
    ) : (
      "-"
    );

  return (
    <tr style={rowStyle}>
      <td style={{ whiteSpace: "nowrap", textAlign: "left" }} className="px-2">
        <OverlayTrigger trigger="click" placement="auto" overlay={NamePopover} rootClose>
          <Button className="p-0" variant="link" style={{ color: linkColor }}>
            {mapName}
          </Button>
        </OverlayTrigger>
      </td>
      <td style={cellStyle} className="text-left ps-2">
        {formatClearDuration(golden.clear_duration)}
      </td>
      <td style={cellStyle}>{proofElement}</td>
    </tr>
  );
}

function ModalContainer({ playerName, modalRefs }) {
  const queryClient = useQueryClient();
  const editModal = useModal(null, (cancelled, mapData) => {
    queryClient.invalidateQueries(["topGoldenList", playerName]);
    if (cancelled) return;
  });

  modalRefs.openEditModal.current = editModal.open;

  return (
    <>
      <Modal show={editModal.isVisible} onHide={editModal.cancel} className="modal-lg">
        <Modal.Header closeButton>
          <Modal.Title>Quick Edit Map</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <QuickEditMap
            playerName={playerName}
            id={editModal.data}
            onSave={(mapData) => editModal.close(false, mapData)}
            onCancel={editModal.cancel}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
